import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import useViewportWidth from "../../hooks/useViewportWidth";
import { businessApi, adminApi } from "../../services/api";
import { backendErrorMessage, isNoBusinessError } from "../../services/onboarding";
import { toast } from "react-toastify";

/**
 * The columns between "Business" and "Status", defined once so the table head, the table body
 * and the phone card view cannot drift apart on a label or a fallback.
 */
const DETAIL_FIELDS = [
  { label: "Email", get: (b) => b.email || "-" },
  { label: "Phone", get: (b) => b.phone || "-" },
  { label: "Address", get: (b) => b.address || "-" },
  // The beer totals, derived by the backend from the beer records (AC-BEERTOTALS-F1). `??`, not
  // `||`: a partner with no beers is a real row of zeros, and only a field the API did not send
  // renders as "-".
  { label: "Total Purchased", get: (b) => b.totalPurchased ?? "-", align: "center" },
  { label: "Total Redeemed", get: (b) => b.totalRedeemed ?? "-", align: "center" },
  { label: "Total Pending", get: (b) => b.totalPending ?? "-", align: "center" },
];

const Dashboard = () => {
  const { user, tokens, isBusinessAdmin, isSuperAdmin } = useAuth();
  // A nine-column table cannot be read on a phone, and a horizontal scroller only makes it
  // reachable, not readable — the row scrolls away from the name it belongs to. Below 900px
  // each row becomes a card instead. AC-MOBILE-F8.
  const isNarrow = useViewportWidth() < 900;
  const navigate = useNavigate();
  const location = useLocation();

  const accessToken = tokens?.access_token;
  const roleKey = isSuperAdmin
    ? "super"
    : isBusinessAdmin
      ? "business"
      : "none";
  // The list's position lives in the URL rather than in component state. Opening a business is a
  // route change to a sibling of this screen, so this component unmounts and everything it held
  // goes with it — `?q=` and `?page=` are what survive the trip and come back. AC-BIZSEARCH-E1.
  const [searchParams, setSearchParams] = useSearchParams();
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const query = searchParams.get("q") || "";
  // Inline styles cannot express :focus, and this field needs a focus ring — it is the one
  // control on the screen that is typed into rather than clicked.
  const [searchFocused, setSearchFocused] = useState(false);
  const rowsPerPage = 5;

  // Filtered here rather than at the API: GET /api/business/all takes no q/page/limit and
  // returns the whole list, which this screen already holds and paginates locally. Searching
  // server-side would leave the row count on the server and the page count on the client —
  // two sources of truth for the same list. When /all grows a page parameter, search moves
  // with it. AC-BIZSEARCH-F1.
  const visibleBusinesses = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allBusinesses;
    return allBusinesses.filter((b) => (b.name || "").toLowerCase().includes(q));
  }, [allBusinesses, query]);

  // Clamped on read and never written back: a URL can say `page=9` for a query with two pages,
  // and an uncorrected cursor renders an empty list next to live page buttons — the failure
  // AC-BIZSEARCH-F2 exists to prevent, arriving by URL instead of by keystroke. Correcting it
  // here rather than in the URL also survives the mount: the list is empty while it loads, and
  // a write-back would flatten `page=3` to 1 before the rows ever arrived. AC-BIZSEARCH-E1.
  const totalPages = Math.max(1, Math.ceil(visibleBusinesses.length / rowsPerPage));
  const currentPage = Math.min(
    Math.max(1, Number(searchParams.get("page")) || 1),
    totalPages
  );

  // Both params are dropped at their defaults, so an untouched list stays at a clean
  // /admin-dashboard. `replace` rather than a push: one history entry per keystroke would make
  // the browser's Back button walk the query back a character at a time. AC-BIZSEARCH-E2.
  const setListPosition = (nextQuery, nextPage) => {
    const next = {};
    if (nextQuery) next.q = nextQuery;
    if (nextPage > 1) next.page = String(nextPage);
    setSearchParams(next, { replace: true });
  };

  // Where a page change lands the reader on a phone: the top of the list, not the top of the page.
  const headingRef = useRef(null);

  const goToPage = (nextPage) => {
    setListPosition(query, nextPage);
    // The page buttons sit below the rows, so reaching them on a phone means scrolling most of a
    // screen down — and tapping one there replaces everything above the reader and leaves them on
    // the last row of a page they have not read a word of. Scrolled to the heading, not to the top
    // of the document: the admin bar above it is navigation rather than part of the list, and
    // pulling that back into view every time spends a fifth of a phone screen on chrome nobody
    // asked for. Narrow only — at 900px and up the rows and the buttons are on screen together, so
    // there is nothing to bring back. AC-BIZSEARCH-F7, AC-BIZSEARCH-E3.
    if (isNarrow) {
      headingRef.current?.scrollIntoView({ block: "start" });
    }
  };

  // Back to page 1 on every keystroke. Without this, narrowing the list while on page 4
  // leaves currentPage past the end of the results and the table renders empty next to live
  // page buttons — indistinguishable from "no matches". AC-BIZSEARCH-F2.
  const applyQuery = (value) => setListPosition(value, 1);

  // Fetch business data based on user role
  useEffect(() => {
    const fetchBusinessData = async () => {
      if (!accessToken) {
        setLoading(false);
        return;
      }

      if (!isBusinessAdmin && !isSuperAdmin) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        if (isBusinessAdmin) {
          // Fetch the business admin's own profile, with its beer totals (AC-BEERTOTALS-F2)
          const response = await businessApi.getMyProfileWithBeerTotals();

          // Transform single business data to array format
          const businessData = [
            {
              name: response.name || "",
              email: response.email || "",
              phone: response.phone || "",
              address: response.address || "",
              status: response.is_active ? "Active" : "Deactivated",
              PK: response.PK,
              ownerName: response.owner_name || "",
              website: response.website || "",
              logoURL: response.logoURL || "",
              description: response.description || "",
              totalPurchased: response.totalPurchased,
              totalRedeemed: response.redeemed,
              totalPending: response.pending,
            },
          ];

          setAllBusinesses(businessData);

          // Auto-select the business for Business Admin (only 1 business)
          // if (businessData.length === 1) {
          //   setSelectedBusiness(businessData[0]);
          // }
        } else if (isSuperAdmin) {
          // Fetch all business profiles, with Stripe status and beer totals, for Super Admin
          const profiles = await adminApi.getBusinessListWithBeerTotals();

          // Unwrap { businesses: [...], count: N } or plain array
          const profileList = Array.isArray(profiles)
            ? profiles
            : Array.isArray(profiles?.businesses)
              ? profiles.businesses
              : [];

          // Transform response to match Dashboard format
          const transformedBusinesses = profileList
            .map((profile) => ({
              name: profile.name || "",
              email: profile.email || "",
              phone: profile.phone || "",
              address: profile.address || "",
              status: profile.isActive || profile.is_active ? "Active" : "Deactivated",
              PK: profile.PK || "",
              ownerName: profile.ownerName || profile.owner_name || "",
              website: profile.website || "",
              logoURL: profile.logoURL || "",
              description: profile.description || "",
              totalPurchased: profile.totalPurchased,
              totalRedeemed: profile.redeemed,
              totalPending: profile.pending,
              stripeVendorId: profile.stripeVendorId || profile.stripe_vendor_id || "",
              payoutsEligible: profile.payoutsEligible ?? false,
            }))
            .sort((a, b) =>
              (a.name || "").localeCompare(b.name || "", undefined, {
                sensitivity: "base",
              }),
            );
          setAllBusinesses(transformedBusinesses);
        }
      } catch (error) {
        console.error("Error fetching business data:", error);

        // A partner who hasn't finished onboarding isn't an error to report at them —
        // send them to the step they still owe. Checked before the toast so they don't
        // get a red banner on the way to a screen they were always meant to see.
        if (isNoBusinessError(error)) {
          navigate("/create-profile");
          return;
        }

        toast.error(
          backendErrorMessage(error) || "Failed to fetch business data",
          {
            position: "top-right",
            autoClose: 4000,
          },
        );

        setAllBusinesses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [accessToken, isBusinessAdmin, isSuperAdmin, roleKey, navigate]);

  const headingStyle = {
    textAlign: "left",
    marginBottom: "30px",
    fontSize: "clamp(24px, 6vw, 40px)",
    fontWeight: "bold",
    color: "#303030",
  };

  // The field is the bordered row, not the input: the magnifier and the clear control sit
  // inside it so the whole thing reads as one control and lights up as one on focus.
  const searchWrapStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    maxWidth: "420px",
    height: "46px",
    padding: "0 14px",
    marginBottom: "24px",
    background: "#FFFFFF",
    border: `1.5px solid ${searchFocused ? "#DE810E" : "#E6E0D8"}`,
    borderRadius: "12px",
    boxShadow: searchFocused
      ? "0 0 0 3px rgba(222, 129, 14, 0.15)"
      : "0 1px 2px rgba(48, 48, 48, 0.05)",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    boxSizing: "border-box",
  };

  const searchInputStyle = {
    flex: 1,
    minWidth: 0,
    height: "100%",
    padding: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#303030",
    // 16px, not 14: below that iOS Safari zooms the viewport on focus and does not zoom back
    // out. Same rule the auth inputs follow. AC-MOBILE-F4.
    fontSize: "16px",
    fontFamily: "inherit",
  };

  const searchClearStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "22px",
    height: "22px",
    padding: 0,
    border: "none",
    borderRadius: "50%",
    background: "rgba(48, 48, 48, 0.07)",
    color: "#6B6B6B",
    cursor: "pointer",
  };

  const paginationBtn = {
    border: "1px solid #ddd",
    padding: "8px 14px",
    borderRadius: "6px",
    cursor: "pointer",
    background: "white",
    fontSize: "14px",
    fontWeight: "500",
  };

  const cellStyle = {
    padding: "18px 24px",
    fontSize: "12px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: "1.4",
    maxHeight: "2.8em",
    overflow: "hidden",
  };

  const businessCellStyle = {
    ...cellStyle,
    maxHeight: "none",
    overflow: "visible",
  };

  const badgeStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "86px",
    height: "32px",
    padding: "0 12px",
    borderRadius: "5px",
    fontSize: "12px",
    fontWeight: "bold",
    whiteSpace: "nowrap",
    lineHeight: 1,
    boxSizing: "border-box",
  };

  const handleRowClick = (business) => {
    // Navigate to business profile detail page
    // Encode the business ID to handle special characters
    const encodedId = encodeURIComponent(business.PK);
    // The list URL rides along so the profile's back control can return to this page of this
    // query rather than to the top of an unfiltered list. AC-BIZSEARCH-F6.
    navigate(`/admin-dashboard/businessProfiles/${encodedId}`, {
      state: { listUrl: `/admin-dashboard${location.search}` },
    });
  };

  // Show loading state
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          fontSize: "18px",
          color: "#777",
        }}
      >
        Loading business data...
      </div>
    );
  }

  // Show empty state
  if (allBusinesses.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <h2 style={headingStyle}>Business</h2>
        <p style={{ fontSize: "18px", color: "#777" }}>
          No business data available.
        </p>
      </div>
    );
  }

  const statusBadge = (business) => (
    <span
      style={{
        ...badgeStyle,
        background: business.status === "Active" ? "#35B44C" : "#CF0A0A",
        color: "white",
      }}
    >
      {business.status}
    </span>
  );

  const stripeBadge = (business) => (
    <span
      style={{
        ...badgeStyle,
        background: business.payoutsEligible ? "#35B44C" : "#CF0A0A",
        color: "white",
      }}
    >
      {business.payoutsEligible
        ? `...${(business.stripeVendorId || "").slice(-4)}`
        : "Not set"}
    </span>
  );

  // Only a super admin has a list worth searching — a business admin's "list" is their own
  // single profile. AC-BIZSEARCH-F4.
  const searchField = isSuperAdmin ? (
    <div style={searchWrapStyle}>
      <svg
        width="17"
        height="17"
        viewBox="0 0 17 17"
        fill="none"
        aria-hidden="true"
        style={{ flexShrink: 0 }}
      >
        <circle
          cx="7.2"
          cy="7.2"
          r="5"
          stroke={searchFocused ? "#DE810E" : "#A79C90"}
          strokeWidth="1.6"
        />
        <path
          d="M11 11L15 15"
          stroke={searchFocused ? "#DE810E" : "#A79C90"}
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
      <input
        type="search"
        value={query}
        onChange={(event) => applyQuery(event.target.value)}
        onFocus={() => setSearchFocused(true)}
        onBlur={() => setSearchFocused(false)}
        placeholder="Search by business name"
        aria-label="Search businesses"
        data-testid="business-search"
        style={searchInputStyle}
      />
      {query && (
        <button
          type="button"
          onClick={() => applyQuery("")}
          aria-label="Clear search"
          data-testid="business-search-clear"
          style={searchClearStyle}
        >
          <svg width="11" height="11" viewBox="0 0 11 11" aria-hidden="true">
            <path
              d="M2 2L9 9M9 2L2 9"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  ) : null;

  // A query that matches nothing says so. Falling through would render column headers over an
  // empty tbody next to pagination arrows for zero pages. The search box stays on screen —
  // it is the only way back. AC-BIZSEARCH-F3.
  if (visibleBusinesses.length === 0) {
    return (
      <>
        <h2 style={headingStyle}>Business</h2>
        {searchField}
        <p data-testid="business-search-empty" style={{ fontSize: "16px", color: "#777" }}>
          No businesses match "{query}".
        </p>
      </>
    );
  }

  const startIdx = (currentPage - 1) * rowsPerPage;
  const paginatedBusinesses = visibleBusinesses.slice(
    startIdx,
    startIdx + rowsPerPage
  );

  return (
    <>
      <h2 ref={headingRef} style={headingStyle}>
        Business
      </h2>
      {searchField}

      {isNarrow ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {paginatedBusinesses.map((business, idx) => (
            <div
              key={idx}
              data-testid="business-card"
              onClick={() => handleRowClick(business)}
              style={{
                border: "1px solid #EEE",
                borderRadius: "12px",
                padding: "16px",
                cursor: "pointer",
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                  {business.logoURL && (
                    <img
                      src={business.logoURL}
                      alt={business.name}
                      style={{
                        width: "32px",
                        height: "32px",
                        objectFit: "cover",
                        borderRadius: "6px",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: "16px",
                      color: "#303030",
                      overflowWrap: "anywhere",
                      lineHeight: 1.3,
                    }}
                  >
                    {business.name}
                  </span>
                </div>
                <div style={{ flexShrink: 0 }}>{statusBadge(business)}</div>
              </div>

              <dl style={{ margin: 0, display: "grid", gap: "8px" }}>
                {DETAIL_FIELDS.map((f) => (
                  <div
                    key={f.label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      gap: "16px",
                      fontSize: "13px",
                    }}
                  >
                    <dt style={{ color: "#777", flexShrink: 0 }}>{f.label}</dt>
                    <dd
                      style={{
                        margin: 0,
                        color: "#303030",
                        fontWeight: 600,
                        textAlign: "right",
                        overflowWrap: "anywhere",
                        minWidth: 0,
                      }}
                    >
                      {f.get(business)}
                    </dd>
                  </div>
                ))}
                {isSuperAdmin && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      gap: "16px",
                      fontSize: "13px",
                    }}
                  >
                    <dt style={{ color: "#777", flexShrink: 0 }}>Stripe</dt>
                    <dd style={{ margin: 0 }}>{stripeBadge(business)}</dd>
                  </div>
                )}
              </dl>
            </div>
          ))}
        </div>
      ) : (
        /* On a wide screen the table stays, and still scrolls inside its own box rather than
           widening the page: it is tableLayout "fixed" with up to 9 columns and 48px of
           padding per cell, so below its minWidth the columns compute to slivers.
           AC-MOBILE-F1. */
        <div
          style={{
            width: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
        <table
          style={{
            minWidth: "900px",
            width: "100%",
            borderCollapse: "collapse",
            borderRadius: "12px",
            overflow: "hidden",
            tableLayout: "fixed",
          }}
        >
          <thead>
            <tr
              style={{
                background: "#DE810E",
                color: "white",
                textAlign: "left",
              }}
            >
              {[
                "Business",
                ...DETAIL_FIELDS.map((f) => f.label),
                "Status",
                ...(isSuperAdmin ? ["Stripe"] : []),
              ].map((header) => (
                <th
                  key={header}
                  style={{
                    padding: "18px 24px",
                    fontSize: "12px",
                    fontWeight: "600",
                    whiteSpace: "normal",
                    ...(header === "Business" ? { width: "20%" } : {}),
                    ...(header === "Status" ? { width: "10%" } : {}),
                    ...(header === "Stripe" ? { width: "10%" } : {}),
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedBusinesses.map((business, idx) => (
              <tr
                key={idx}
                style={{
                  borderBottom: "1px solid #eee",
                  cursor: "pointer",
                }}
                onClick={() => handleRowClick(business)}
              >
                <td style={businessCellStyle}>
                  <div
                    style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}
                  >
                    {business.logoURL && (
                      <img
                        src={business.logoURL}
                        alt={business.name}
                        style={{
                          width: "30px",
                          height: "30px",
                          objectFit: "cover",
                          borderRadius: "4px",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                        lineHeight: "1.35",
                      }}
                    >
                      {business.name}
                    </span>
                  </div>
                </td>
                {DETAIL_FIELDS.map((f) => (
                  <td
                    key={f.label}
                    style={
                      f.align === "center"
                        ? { ...cellStyle, textAlign: "center" }
                        : cellStyle
                    }
                  >
                    {f.get(business)}
                  </td>
                ))}
                <td style={{ ...cellStyle, textAlign: "center" }}>
                  {statusBadge(business)}
                </td>
                {isSuperAdmin && (
                  <td style={{ ...cellStyle, textAlign: "center" }}>
                    {stripeBadge(business)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {/* ✅ Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "40px",
          gap: "10px",
        }}
      >
        {/* Prev Button */}
        <button
          disabled={currentPage === 1}
          onClick={() => goToPage(currentPage - 1)}
          style={{
            ...paginationBtn,
            background: "rgba(222, 129, 14, 0.1)",
            border: "1px solid #DE810E",
            color: "black",
            fontWeight: "bold",
            fontSize: "18px",
          }}
        >
          ‹
        </button>

        {/* Page Numbers - always show max 5 */}
        {(() => {
          let pages = [];

          if (totalPages <= 5) {
            pages = Array.from({ length: totalPages }, (_, i) => i + 1);
          } else if (currentPage <= 3) {
            pages = [1, 2, 3, 4, 5];
          } else if (currentPage >= totalPages - 2) {
            pages = [
              totalPages - 4,
              totalPages - 3,
              totalPages - 2,
              totalPages - 1,
              totalPages,
            ];
          } else {
            pages = [
              currentPage - 2,
              currentPage - 1,
              currentPage,
              currentPage + 1,
              currentPage + 2,
            ];
          }

          return pages.map((num) => {
            const isActive = num === currentPage;
            return (
              <button
                key={num}
                onClick={() => goToPage(num)}
                style={{
                  ...paginationBtn,
                  border: `1px solid ${isActive ? "#DE810E" : "#303030"}`,
                  background: "white",
                  color: isActive ? "#DE810E" : "#303030",
                  fontWeight: "bold",
                  opacity: isActive ? 1 : 0.5,
                }}
              >
                {num}
              </button>
            );
          });
        })()}

        {/* Next Button */}
        <button
          disabled={currentPage === totalPages}
          onClick={() => goToPage(currentPage + 1)}
          style={{
            ...paginationBtn,
            background: "rgba(222, 129, 14, 0.1)",
            border: "1px solid #DE810E",
            color: "black",
            fontWeight: "bold",
            fontSize: "18px",
          }}
        >
          ›
        </button>
      </div>
    </>
  );
};

export default Dashboard;

